import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { getUserOrganization, getSearchTerms } from '../../firebase/firestore';
import {
  TrendingUp,
  TrendingDown,
  Search,
  Plus,
  Sparkles,
  ExternalLink,
  AlertCircle,
  Lock,
  Zap
} from 'lucide-react';

// Subscription tiers that have access to AI insights
const AI_INSIGHTS_TIERS = ['pro', 'business', 'enterprise'];

function GEODashboard() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [searchTerms, setSearchTerms] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [organization, setOrganization] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);

  // Check if user has access to AI insights based on their subscription tier
  const userTier = userProfile?.subscriptionTier || userProfile?.plan || 'free';
  const hasAIInsightsAccess = AI_INSIGHTS_TIERS.includes(userTier);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      // Get user's organization
      const org = await getUserOrganization(user.uid);
      if (!org) {
        console.log('No organization found');
        setLoading(false);
        return;
      }
      setOrganization(org);

      // Get search terms for organization
      const terms = await getSearchTerms(org.id);
      setSearchTerms(terms);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const avgPosition = searchTerms.length > 0
      ? (searchTerms.reduce((acc, t) => acc + (t.position || 0), 0) / searchTerms.length).toFixed(1)
      : '0';

    const totalCitations = searchTerms.reduce((acc, t) => acc + (t.citations || 0), 0);

    const top5Count = searchTerms.filter(t => t.position && t.position <= 5).length;

    return [
      {
        label: 'Avg. Position',
        value: avgPosition,
        change: '-',
        isPositive: true
      },
      {
        label: 'Total Citations',
        value: totalCitations.toString(),
        change: '-',
        isPositive: true
      },
      {
        label: 'Tracked Terms',
        value: searchTerms.length,
        change: '-',
        isPositive: true
      },
      {
        label: 'Top 5 Rankings',
        value: top5Count.toString(),
        change: '-',
        isPositive: true
      }
    ];
  };

  const stats = calculateStats();

  const getPositionColor = (position) => {
    if (position <= 3) return 'text-green-400';
    if (position <= 5) return 'text-blue-400';
    if (position <= 10) return 'text-yellow-400';
    return 'text-gray-400';
  };

  const getChangeIcon = (change) => {
    if (change > 0) return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (change < 0) return <TrendingDown className="w-4 h-4 text-red-400" />;
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-white mb-2">
          GEO Performance Dashboard
        </h1>
        <p className="text-gray-400">Track your rankings across generative AI search engines</p>
      </motion.div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-400">{stat.label}</p>
              <span className={`text-sm font-medium ${stat.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                {stat.change}
              </span>
            </div>
            <h3 className="text-3xl font-bold text-white">{stat.value}</h3>
          </motion.div>
        ))}
      </div>

      {/* Search Terms Tracking */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Search Term Rankings</h2>
          <button
            onClick={() => navigate('/dashboard/search-terms')}
            className="flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-secondary-500 px-4 py-2 rounded-lg hover:shadow-lg transition-all"
          >
            <Plus className="w-4 h-4" />
            <span>Add Term</span>
          </button>
        </div>

        {searchTerms.length === 0 ? (
          <div className="text-center py-12">
            <Search className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-semibold text-white mb-2">No search terms yet</h3>
            <p className="text-gray-400 mb-6">
              Add your first search term to start tracking your GEO performance
            </p>
            <button
              onClick={() => navigate('/dashboard/search-terms')}
              className="bg-gradient-to-r from-primary-500 to-secondary-500 px-6 py-3 rounded-lg hover:shadow-lg transition-all inline-flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Add Search Term</span>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {searchTerms.map((term, index) => (
              <motion.div
                key={term.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="bg-dark-700/50 border border-white/10 rounded-lg p-6 hover:border-primary-500/30 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Search className="w-5 h-5 text-primary-400" />
                      <h3 className="text-lg font-semibold text-white">{term.term}</h3>
                      {term.change && term.change !== 0 && (
                        <div className="flex items-center space-x-1">
                          {getChangeIcon(term.change)}
                          <span className={`text-sm ${term.change > 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {Math.abs(term.change)}
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">Last checked: {term.lastChecked || 'Never'}</p>
                  </div>
                  <div className="text-right">
                    <div className={`text-3xl font-bold ${getPositionColor(term.position || 0)}`}>
                      {term.position ? `#${term.position}` : '-'}
                    </div>
                    <p className="text-sm text-gray-400">Avg. Position</p>
                  </div>
                </div>

                {/* Engine Breakdown */}
                {term.engines && Object.keys(term.engines).length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(term.engines).map(([engine, position]) => (
                      <div key={engine} className="bg-dark-800/50 rounded-lg p-3">
                        <p className="text-xs text-gray-400 capitalize mb-1">{engine}</p>
                        <p className={`text-lg font-bold ${getPositionColor(position)}`}>
                          #{position}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-dark-800/50 rounded-lg p-4 text-center text-gray-400 text-sm">
                    Rankings will appear here after the first check
                  </div>
                )}

                {/* Citations */}
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm text-gray-400">
                    <Sparkles className="w-4 h-4 text-primary-400" />
                    <span>{term.citations || 0} citations found</span>
                  </div>
                  <button className="flex items-center space-x-2 text-sm text-primary-400 hover:text-primary-300 transition-colors">
                    <span>View Details</span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* AI Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className={`border rounded-xl p-6 ${
          hasAIInsightsAccess
            ? 'bg-gradient-to-r from-primary-500/10 to-secondary-500/10 border-primary-500/30'
            : 'bg-dark-800/50 border-white/10'
        }`}
      >
        <div className="flex items-start space-x-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
            hasAIInsightsAccess
              ? 'bg-gradient-to-r from-primary-500 to-secondary-500'
              : 'bg-dark-700 border border-white/10'
          }`}>
            {hasAIInsightsAccess ? (
              <Sparkles className="w-6 h-6 text-white" />
            ) : (
              <Lock className="w-6 h-6 text-gray-400" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white mb-2">AI-Powered Insights</h3>

            {hasAIInsightsAccess ? (
              <>
                <p className="text-gray-300 mb-4">
                  Based on your recent performance, here are recommendations to improve your GEO rankings:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2 text-sm text-gray-300">
                    <AlertCircle className="w-4 h-4 text-primary-400 mt-0.5 flex-shrink-0" />
                    <span>Your "enterprise collaboration platform" term is trending down. Consider updating content with recent case studies.</span>
                  </li>
                  <li className="flex items-start space-x-2 text-sm text-gray-300">
                    <AlertCircle className="w-4 h-4 text-primary-400 mt-0.5 flex-shrink-0" />
                    <span>Great momentum on "best project management software" - maintain citation sources and update feature comparisons.</span>
                  </li>
                </ul>
                <button className="mt-4 text-sm text-primary-400 hover:text-primary-300 font-medium">
                  View all insights →
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-400 mb-4">
                  Unlock AI-powered recommendations to improve your GEO rankings. Get personalized insights based on your search term performance, competitor analysis, and industry trends.
                </p>
                <div className="bg-dark-700/50 rounded-lg p-4 mb-4 border border-white/5">
                  <p className="text-sm text-gray-500 mb-3">With AI Insights, you'll get:</p>
                  <ul className="space-y-2 text-sm text-gray-400">
                    <li className="flex items-center space-x-2">
                      <Zap className="w-4 h-4 text-primary-400" />
                      <span>Personalized ranking improvement recommendations</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Zap className="w-4 h-4 text-primary-400" />
                      <span>Competitor gap analysis and opportunities</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Zap className="w-4 h-4 text-primary-400" />
                      <span>Content optimization suggestions</span>
                    </li>
                  </ul>
                </div>
                <button
                  onClick={() => navigate('/dashboard/settings/billing')}
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-secondary-500 px-5 py-2.5 rounded-lg hover:shadow-lg transition-all text-sm font-medium"
                >
                  <Zap className="w-4 h-4" />
                  <span>Upgrade to Pro</span>
                </button>
                <p className="mt-3 text-xs text-gray-500">
                  Available on Pro, Business, and Enterprise plans
                </p>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default GEODashboard;
